import React, { useState, useEffect, useCallback } from "react";
import CreateAmenityCategoryModal from "./createAmenityCategory";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  deleteAmenityCategory,
  getAllAmenityCategories,
} from "../../../../../../../apis/property";
import EditAmenityCategoryModal from "./editAmenityCategory";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  SparklesIcon,
  SearchIcon,
  RefreshIcon,
} from "@heroicons/react/outline";

const AmenityCategory = () => {
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [amentityCategory, setAmentityCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAmenityCategoryId, setSelectedAmenityCategoryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchAmenityCategory = useCallback(async () => {
    setLoading(true);
    try {
      if (!id) return;
      const response = await getAllAmenityCategories(id);
      setAmentityCategory(response.data);
    } catch (error) {
      console.log("Error fetching Amenity Category");
      toast.error("Failed to load amenity categories");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAmenityCategory();
  }, [fetchAmenityCategory]);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = async () => {
    setIsModalOpen(false);
    await fetchAmenityCategory();
  };

  const openEditModal = (amenity_category_id) => {
    setSelectedAmenityCategoryId(amenity_category_id);
    setIsEditModalOpen(true);
  };

  const closeEditModal = async () => {
    setIsEditModalOpen(false);
    await fetchAmenityCategory();
    setSelectedAmenityCategoryId(null);
  };

  const handleDelete = async (amenity_category_id) => {
    if (window.confirm("Are you sure you want to delete this amenity category?")) {
      try {
        await deleteAmenityCategory(id, amenity_category_id);
        toast.success("Amenity Category deleted successfully");
        await fetchAmenityCategory();
      } catch (error) {
        toast.error("Failed to delete Amenity Category");
      }
    }
  };

  // Filter categories based on search
  const filteredCategories = amentityCategory?.filter((category) =>
    category.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="amenity-container">
      {/* Header Section */}
      <div className="header-section">
        <div className="header-content">
          <div className="header-icon-wrapper">
            <SparklesIcon className="header-icon" />
          </div>
          <div>
            <h1 className="page-title">Amenity Categories</h1>
            <p className="page-subtitle">Manage and organize your property amenities</p>
          </div>
        </div>
        <button onClick={openModal} className="add-button">
          <PlusIcon className="w-5 h-5" />
          <span>Add Category</span>
        </button>
      </div>

      {/* Stats Card */}
      <div className="stats-card">
        <div className="stat-item">
          <div className="stat-icon stat-icon-purple">
            <SparklesIcon className="w-5 h-5" />
          </div>
          <div>
            <p className="stat-label">Total Categories</p>
            <h3 className="stat-value">{amentityCategory?.length || 0}</h3>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-wrapper">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <button onClick={fetchAmenityCategory} className="refresh-button">
          <RefreshIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Table Section */}
      <div className="table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading amenity categories...</p>
          </div>
        ) : filteredCategories && filteredCategories.length > 0 ? (
          <table className="amenity-table">
            <thead>
              <tr>
                <th>Category Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map((amenityCategory) => (
                <tr key={amenityCategory.id}>
                  <td>
                    <div className="category-name">
                      <div className="category-icon">
                        <SparklesIcon className="w-5 h-5" />
                      </div>
                      <span className="font-semibold">{amenityCategory.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="description-text">{amenityCategory.description}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => openEditModal(amenityCategory.id)}
                        className="action-btn action-btn-edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => handleDelete(amenityCategory.id)}
                        className="action-btn action-btn-delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <SparklesIcon className="w-16 h-16 text-gray-400" />
            <h3>No Amenity Categories Found</h3>
            <p>
              {searchQuery
                ? "Try adjusting your search criteria"
                : "Get started by adding your first amenity category"}
            </p>
            {!searchQuery && (
              <button onClick={openModal} className="add-button">
                <PlusIcon className="w-5 h-5" />
                <span>Add Category</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateAmenityCategoryModal isOpen={isModalOpen} onClose={closeModal} />
      <EditAmenityCategoryModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        selectedAmenityCategoryId={selectedAmenityCategoryId}
      />

      {/* Styles */}
      <style jsx>{`
        .amenity-container {
          padding: 0;
        }

        .header-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon-wrapper {
          width: 4rem;
          height: 4rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.3);
        }

        .header-icon {
          width: 2rem;
          height: 2rem;
          color: white;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.25rem 0;
        }

        .page-subtitle {
          font-size: 1rem;
          color: #6b7280;
          margin: 0;
        }

        .add-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          font-weight: 600;
          border-radius: 0.75rem;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3);
        }

        .add-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
        }

        .stats-card {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          margin-bottom: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .stat-icon {
          width: 3rem;
          height: 3rem;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .stat-icon-purple {
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
        }

        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 0.25rem 0;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
        }

        .search-section {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .search-wrapper {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          width: 1.25rem;
          height: 1.25rem;
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 3rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .refresh-button {
          padding: 0.75rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .refresh-button:hover {
          border-color: #8b5cf6;
          color: #8b5cf6;
          transform: rotate(180deg);
        }

        .table-container {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
        }

        .spinner {
          width: 3rem;
          height: 3rem;
          border: 4px solid #e5e7eb;
          border-top-color: #8b5cf6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .amenity-table {
          width: 100%;
          border-collapse: collapse;
        }

        .amenity-table thead {
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .amenity-table th {
          padding: 1rem 1.5rem;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .amenity-table td {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f3f4f6;
        }

        .amenity-table tbody tr {
          transition: background 0.2s;
        }

        .amenity-table tbody tr:hover {
          background: #f9fafb;
        }

        .category-name {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .category-icon {
          width: 2.5rem;
          height: 2.5rem;
          background: linear-gradient(135deg, #ede9fe, #ddd6fe);
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #8b5cf6;
        }

        .description-text {
          color: #6b7280;
          font-size: 0.875rem;
          max-width: 500px;
          display: block;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .action-btn-edit {
          background: #eff6ff;
          color: #2563eb;
        }

        .action-btn-edit:hover {
          background: #dbeafe;
          transform: translateY(-1px);
        }

        .action-btn-delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .action-btn-delete:hover {
          background: #fee2e2;
          transform: translateY(-1px);
        }

        .empty-state {
          padding: 4rem 2rem;
          text-align: center;
        }

        .empty-state h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          margin: 1rem 0 0.5rem 0;
        }

        .empty-state p {
          color: #6b7280;
          margin: 0 0 1.5rem 0;
        }

        @media (max-width: 768px) {
          .header-section {
            flex-direction: column;
            align-items: flex-start;
          }

          .add-button {
            width: 100%;
            justify-content: center;
          }

          .page-title {
            font-size: 1.5rem;
          }

          .search-section {
            flex-direction: column;
          }

          .search-wrapper {
            max-width: 100%;
          }

          .amenity-table {
            display: block;
            overflow-x: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default AmenityCategory;
